import { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  getNotifications, getUnreadCount, markNotificationRead, markAllNotificationsRead,
} from '../api/client'

const POLL_MS = 45000
const ANCHO_PANEL = 320

function relativeTime(iso, tFn) {
  if (!iso) return ''
  const then = new Date(iso).getTime()
  const diff = Math.max(0, Date.now() - then)
  const m = Math.floor(diff / 60000)
  if (m < 1) return tFn('notifications.timeJustNow')
  if (m < 60) return tFn('notifications.timeMinutes', { count: m })
  const h = Math.floor(m / 60)
  if (h < 24) return tFn('notifications.timeHours', { count: h })
  const d = Math.floor(h / 24)
  return tFn('notifications.timeDays', { count: d })
}

function BellIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.73 21a2 2 0 0 1-3.46 0" />
    </svg>
  )
}

// The bell with the number of notifications not yet read. `variante="icono"` is the small button that sits next
// to the person's name (its panel opens above it, wherever it is); "fila" is the old row of the sidebar menu.
export default function NotificationBell({ collapsed = false, variante = 'fila' }) {
  const { t } = useTranslation(['settings'])
  const navigate = useNavigate()
  const [count, setCount] = useState(0)
  const [open, setOpen] = useState(false)
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(false)
  const [pos, setPos] = useState(null)
  const ref = useRef(null)
  const boton = useRef(null)
  const icono = variante === 'icono'

  const refreshCount = useCallback(() => {
    getUnreadCount().then(d => setCount(d?.count || 0)).catch(() => {})
  }, [])

  useEffect(() => {
    refreshCount()
    const id = setInterval(refreshCount, POLL_MS)
    window.addEventListener('focus', refreshCount)
    return () => { clearInterval(id); window.removeEventListener('focus', refreshCount) }
  }, [refreshCount])

  useEffect(() => {
    if (!open) return
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    const esc = (e) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', handler)
    document.addEventListener('keydown', esc)
    return () => { document.removeEventListener('mousedown', handler); document.removeEventListener('keydown', esc) }
  }, [open])

  const togglePanel = () => {
    const next = !open
    setOpen(next)
    if (next) {
      if (icono && boton.current) {
        const r = boton.current.getBoundingClientRect()
        setPos({ left: Math.max(8, Math.min(r.left, window.innerWidth - ANCHO_PANEL - 8)), bottom: window.innerHeight - r.top + 8 })
      }
      setLoading(true)
      getNotifications(30)
        .then(d => setItems(Array.isArray(d) ? d : []))
        .catch(() => setItems([]))
        .finally(() => setLoading(false))
    }
  }

  const onItemClick = async (n) => {
    setOpen(false)
    if (!n.is_read) {
      try { await markNotificationRead(n.id) } catch { /* it stays unread: the count refreshes below */ }
      setItems(prev => prev.map(x => x.id === n.id ? { ...x, is_read: true } : x))
      refreshCount()
    }
    if (n.link) navigate(n.link)
  }

  const onMarkAll = async () => {
    try { await markAllNotificationsRead() } catch { /* the count is reset below either way */ }
    setItems(prev => prev.map(x => ({ ...x, is_read: true })))
    setCount(0)
  }

  const globo = (cls) => count > 0 && (
    <span className={`${cls} min-w-[16px] h-[16px] px-1 rounded-full bg-red-500 text-white text-[10px] font-semibold flex items-center justify-center`}
      aria-hidden="true">
      {count > (icono ? 99 : 9) ? (icono ? '99+' : '9+') : count}
    </span>
  )

  return (
    <div className="relative" ref={ref}>
      {icono ? (
        <button ref={boton} onClick={togglePanel} aria-expanded={open} aria-haspopup="true"
          title={count > 0 ? t('notifications.unread', { count }) : t('notifications.buttonTitle')}
          aria-label={count > 0 ? t('notifications.unread', { count }) : t('notifications.buttonTitle')}
          className="relative flex items-center justify-center w-8 h-8 rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-800 transition-colors">
          <BellIcon />
          {globo('absolute -top-0.5 -right-0.5')}
        </button>
      ) : (
        <button
          onClick={togglePanel}
          title={t('notifications.buttonTitle')}
          className={`relative flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-sm transition-colors w-full
            text-gray-500 hover:bg-gray-50 hover:text-gray-800 ${collapsed ? 'justify-center' : ''}`}
        >
          <span className="shrink-0"><BellIcon /></span>
          {!collapsed && <span>{t('notifications.title')}</span>}
          {globo(`absolute ${collapsed ? 'top-1 right-1' : 'right-2.5 top-1.5'}`)}
        </button>
      )}

      {open && (
        <div
          style={icono && pos ? { position: 'fixed', left: pos.left, bottom: pos.bottom, width: ANCHO_PANEL } : undefined}
          className={`${icono ? 'z-50' : 'absolute bottom-full mb-1 left-0 w-[300px] z-30'} max-h-[420px] bg-white
          border border-gray-200 rounded-lg shadow-lg overflow-hidden flex flex-col`}>
          <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100">
            <span className="text-sm font-semibold text-gray-800">
              {t('notifications.title')}
              {count > 0 && <span className="ml-2 text-xs font-normal text-red-600">{t('notifications.unread', { count })}</span>}
            </span>
            <button onClick={onMarkAll}
              className="text-[11px] text-gray-400 hover:text-gray-700">{t('notifications.markAllRead')}</button>
          </div>
          <div className="overflow-y-auto">
            {loading && <p className="px-3 py-4 text-sm text-gray-400">{t('notifications.loading')}</p>}
            {!loading && items.length === 0 && (
              <p className="px-3 py-6 text-sm text-gray-400 text-center">{t('notifications.empty')}</p>
            )}
            {!loading && items.map(n => (
              <button key={n.id} onClick={() => onItemClick(n)}
                className={`w-full text-left px-3 py-2.5 border-b border-gray-50 hover:bg-gray-50
                  transition-colors ${n.is_read ? '' : 'bg-blue-50/40'}`}>
                <div className="flex items-start gap-2">
                  {!n.is_read && <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-blue-500 shrink-0" />}
                  <div className="min-w-0 flex-1">
                    <p className={`text-sm text-gray-900 font-medium ${n.type === 'mention' ? '' : 'truncate'}`}>
                      {n.type === 'mention' && <span aria-hidden="true">@ </span>}{n.title}
                    </p>
                    {n.body && <p className={`text-xs text-gray-500 ${n.type === 'mention' ? 'line-clamp-2' : 'truncate'}`}>{n.body}</p>}
                    <p className="text-[10px] text-gray-400 mt-0.5">{relativeTime(n.created_at, t)}</p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
